/**
 * Live-Suche im Such-Panel des Headers.
 * Die Treffer kommen als fertiges HTML über die Section Rendering API
 * (sections/predictive-search.liquid), damit Preise dieselbe Logik wie die
 * Produktkarten nutzen. Ergebnisse je Suchbegriff werden zwischengespeichert,
 * eine ältere Anfrage wird abgebrochen, sobald weitergetippt wird.
 */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.predictive-search__input');
    this.results = this.querySelector('.predictive-search__results');
    this.status = this.querySelector('.predictive-search__status');
    if (!this.input || !this.results) return;

    this.cache = new Map();
    this.controller = null;

    // Eigenes Debounce statt window.ShirtWerk.debounce: dieses Skript steht im
    // Header und läuft vor theme.js, window.ShirtWerk gibt es hier noch nicht.
    let timer;
    this.input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this._search(), 200);
    });
    this.input.addEventListener('focus', () => {
      if (this.results.innerHTML.trim() && this.input.value.trim().length >= 2) this._open();
    });
    this.addEventListener('keydown', (e) => this._onKeydown(e));
  }

  reset() {
    this.input.value = '';
    this.results.innerHTML = '';
    this._close();
  }

  async _search() {
    const term = this.input.value.trim();
    if (term.length < 2) {
      if (this.controller) this.controller.abort();
      this._close();
      return;
    }
    if (this.cache.has(term)) {
      this._render(this.cache.get(term));
      return;
    }

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    this.setAttribute('loading', '');
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,page',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });
    try {
      const response = await fetch(`${window.Shopify.routes.root}search/suggest?${params}`, {
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error('predictive-search-failed');
      const html = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('#shopify-section-predictive-search');
      const markup = html ? html.innerHTML : '';
      this.cache.set(term, markup);
      // Antwort für einen inzwischen geänderten Begriff nicht mehr anzeigen
      if (this.input.value.trim() === term) this._render(markup);
    } catch (err) {
      if (err.name !== 'AbortError') this._close();
    } finally {
      this.removeAttribute('loading');
    }
  }

  _render(markup) {
    this.results.innerHTML = markup;
    if (!markup.trim()) {
      this._close();
      return;
    }
    const box = this.results.querySelector('[data-ps-count]');
    const count = box ? Number(box.dataset.psCount) : 0;
    this.status.textContent = count ? `${count} Treffer` : this.results.querySelector('.ps-results__empty')?.textContent || '';
    this._open();
  }

  _items() {
    return Array.from(this.results.querySelectorAll('[data-ps-item]'));
  }

  _onKeydown(e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = this._items();
    if (this.results.hidden || !items.length) return;
    e.preventDefault();
    const index = items.indexOf(document.activeElement);
    let next;
    if (e.key === 'ArrowDown') next = index < items.length - 1 ? index + 1 : 0;
    else next = index > 0 ? index - 1 : -1;
    if (next === -1) this.input.focus();
    else items[next].focus();
  }

  _open() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  _close() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
